import { LiliTextInput } from '@lili/lili-frontend-common';
import { t } from 'i18next';
import { normalizeInputSpacesOnBlur, normalizeInputSpacesOnChange } from '@lili/utils/formatting';
import { ControllerWrapper } from '@Hooks/index';
import { OnboardingFormFields } from '@Types/index';
import { getRouteOrdinal, RoutesMap } from '@Router/router.types';
import {
  AddressStateInput,
  useHandleOnCheckedChange,
} from '@Components/inputs/AddressStateDropDown';
import { ZipInput } from '@Components/inputs/ZipAddress';
import { Line1AutocompleteInput } from '@Components/inputs/Line1Autocomplete/Line1AutocompleteInput';
import { SameAddressCheckbox } from '@Components/inputs/SameAddressCheckbox/SameAddressCheckbox';
import type { AddressData } from '@Store/index';
import type { IRegisteredAddressForm } from './registeredAddressSchema';
import type { ExtendedUseFormReturn } from '@Hooks/index';

interface IRegisteredAddressFormProps {
  formMethods: ExtendedUseFormReturn<IRegisteredAddressForm>;
  defaultValues: IRegisteredAddressForm;
  customerAddressData: AddressData;
  isHomeAddressUS: boolean;
  usStates?: { title: string; value: string }[];
}

export const RegisteredAddressForm = ({
  formMethods,
  defaultValues,
  customerAddressData,
  isHomeAddressUS,
  usStates,
}: IRegisteredAddressFormProps) => {
  const { control, setValue, trigger, watch } = formMethods;
  const baseTabIndex = getRouteOrdinal(RoutesMap.REGISTERED_ADDRESS);

  const { line1, line2, postalCode, state, city } = customerAddressData;
  const { handleOnCheckChange } = useHandleOnCheckedChange({
    setValue,
    state,
    line1,
    city,
    postalCode,
    line2,
    trigger,
    formMethods,
    defaultValues,
    validStates: usStates,
  });

  const showSameAsPersonal = isHomeAddressUS;
  const isSameAddress = watch(OnboardingFormFields.REGISTERED_ADDRESS_SAME_AS_PRIMARY);

  return (
    <div>
      {showSameAsPersonal && (
        <SameAddressCheckbox
          control={control}
          fieldName={OnboardingFormFields.REGISTERED_ADDRESS_SAME_AS_PRIMARY}
          label={t('pages.registeredAddress.sameAsPersonal')}
          onChange={handleOnCheckChange}
        />
      )}

      <Line1AutocompleteInput
        tabIndex={baseTabIndex}
        disabled={isSameAddress}
        validStates={usStates}
      />

      <div className='flex flex-col items-start w-full'>
        <div className='w-full'>
          <ControllerWrapper name={OnboardingFormFields.APARTMENT}>
            {({ value, onBlur, onChange, error }) => (
              <LiliTextInput
                value={value}
                onBlur={onBlur}
                onChange={onChange}
                id={OnboardingFormFields.APARTMENT}
                title={t('pages.homeAddress.lineTwoFieldTitle')}
                errorMessage={error?.message}
                tabIndex={baseTabIndex + 1}
                disabled={isSameAddress}
                isPublic
              />
            )}
          </ControllerWrapper>
        </div>

        <div className='w-full'>
          <ControllerWrapper name={OnboardingFormFields.CITY}>
            {({ value, onBlur, onChange, error }) => (
              <LiliTextInput
                value={value}
                onBlur={() => {
                  onChange(normalizeInputSpacesOnBlur(value));
                  onBlur();
                }}
                onChange={(val) => onChange(normalizeInputSpacesOnChange(val))}
                id={OnboardingFormFields.CITY}
                title={t('pages.homeAddress.cityFieldTitle')}
                errorMessage={error?.message}
                disabled={isSameAddress}
                tabIndex={baseTabIndex + 2}
                isPublic
              />
            )}
          </ControllerWrapper>
        </div>
      </div>

      <div className='w-full flex sm:flex-row flex-col sm:gap-[16px]'>
        <div className='flex-1' data-testid='state_dropdown_wrapper'>
          <AddressStateInput
            disabled={isSameAddress}
            tabIndex={baseTabIndex + 3}
            statesList={usStates}
          />
        </div>

        <div className='flex-1'>
          <ZipInput disabled={isSameAddress} tabIndex={baseTabIndex + 4} isUS={true} />
        </div>
      </div>
    </div>
  );
};
